import { LANGUAGES } from '@vindr/services/constants/common.constant';
import { DefinedColumns } from '@vindr/services/interfaces';
import unitToMillis from '@vindr/utils/date/unitToMillis';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ElapsedTimeColumnProps, UpdateDate } from './ElapsedTimeColumn'; // Adjust the import path as necessary

/**
 * Props interface for WaitingTimeColumn.
 * Extends CommonElapsedTimeColumnInterface with additional properties if needed.
 */
export type WaitingTimeColumnProps<T> = ElapsedTimeColumnProps<T> & {
  resultIndex?: keyof T; // Optional field for result approved date
};

/**
 * WaitingTimeColumn: Extends ElapsedTimeColumn to handle additional logic for wait time calculation.
 *
 * @param props - Properties including dataIndex, endIndex, resultIndex, etc.
 * @returns DefinedColumns<T> - Column definition for the table.
 */
const WaitingTimeColumn = <T,>(props: WaitingTimeColumnProps<T>): DefinedColumns<T> => {
  const {
    title,
    dataIndex = 'patient_arrived_time' as keyof T,
    endIndex, // Typically 'started_at'
    resultIndex = 'result_approved_at' as keyof T, // Field for result approved date
    requirement,
    unit,
    detailed,
    format,
    interval,
    width = 70,
    key = 'wait_time' as keyof T,
    sorter,
    ellipsis = true,
    render,
    ...rest
  } = props || {};

  const { t, i18n } = useTranslation('Vindoc');

  /**
   * Custom render function to handle new wait time calculation logic.
   *
   * @param startDate - The patient_arrived_time
   * @param record - The entire record containing started_at and result_approved_at
   * @returns JSX.Element - Rendered wait time
   */
  const customRender = (startDate: any, record: T) => {
    // Determine the appropriate end date based on the new requirements
    const patientArrivedTime = record[dataIndex] as string | undefined;
    const startedAt = record[endIndex] as string | undefined;
    const resultApprovedAt = resultIndex ? (record[resultIndex] as string | undefined) : undefined;

    let effectiveWaitDate: string | undefined;

    if (!patientArrivedTime) {
      // Thời gian tiếp nhận = null → Thời gian chờ = null
      return <span></span>;
    }

    if (startedAt) {
      // Thời gian bắt đầu khác null → Thời gian chờ = Thời gian bắt đầu - Thời gian tiếp nhận
      effectiveWaitDate = startedAt;
    } else if (resultApprovedAt) {
      // Thời gian bắt đầu = null && Thời gian duyệt kết quả != null
      // → Thời gian chờ = Thời gian duyệt kết quả - Thời gian tiếp nhận
      effectiveWaitDate = resultApprovedAt;
    } else if (patientArrivedTime) {
      // Thời gian bắt đầu = null && Thời gian duyệt kết quả = null
      // → Thời gian chờ = Now - Thời gian tiếp nhận
      effectiveWaitDate = null; //? Set Date.now inside UpdateDate
    }

    return (
      <UpdateDate
        endDate={effectiveWaitDate}
        interval={typeof interval === 'number' ? interval : unitToMillis(unit)}
        startDate={patientArrivedTime}
        {...{ requirement, unit, detailed, format }}
      />
    );
  };

  return {
    title: title || t('Wait Time'),
    dataIndex,
    key,
    width: i18n.language === LANGUAGES.VI ? Number(width) + 50 : width,
    sorter,
    ellipsis,
    render: render || customRender,
    ...rest,
  };
};

export default WaitingTimeColumn;
